import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Avatar from '@decisiv/ui-components/lib/components/Avatar';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import Select from '@decisiv/ui-components/lib/components/Select';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import iconix from '@decisiv/iconix';

import AccountsField from '../../../shared/AccountsField';
import { ADD_TO_ACCOUNT } from './copy';
import { AddToAccountIdentity } from './styles';

const ALL_TEAMS_ID = 'all';
const TEAM_OPTIONS = [
  {
    id: ALL_TEAMS_ID,
    label: ADD_TO_ACCOUNT.teamPlaceholder,
    value: ALL_TEAMS_ID,
  },
];

const AddToAccountModal = ({
  accountOptions,
  accountsTotal,
  email,
  error,
  fullName,
  hasMoreAccounts,
  isLoadingAccounts,
  isSubmitting,
  onChangeSelectedAccounts,
  onClose,
  onLoadMoreAccounts,
  onSearchAccounts,
  onSubmit,
  selectedAccountIds,
  visible,
}) => {
  return (
    <Modal
      actions={[
        {
          text: ADD_TO_ACCOUNT.submit,
          onClick: onSubmit,
          kind: 'primary',
          loading: isSubmitting,
          loadingText: ADD_TO_ACCOUNT.submitting,
          disabled: selectedAccountIds.length === 0,
        },
        {
          text: ADD_TO_ACCOUNT.cancel,
          onClick: onClose,
          kind: 'secondary',
          disabled: isSubmitting,
        },
      ]}
      intent="information"
      icon={iconix.UserPlus}
      onClose={onClose}
      title={ADD_TO_ACCOUNT.title}
      visible={visible}
    >
      <Flex flexDirection="column" justifyContent="center">
        <P shade={1}>{ADD_TO_ACCOUNT.subtitle}</P>

        <AddToAccountIdentity>
          <Avatar email={email} name={fullName} />
          <P weight="semibold">{fullName}</P>
        </AddToAccountIdentity>

        <Select
          helpMessage={ADD_TO_ACCOUNT.teamHelp}
          label={ADD_TO_ACCOUNT.team}
          name="team"
          options={TEAM_OPTIONS}
          value={ALL_TEAMS_ID}
          onChange={() => {}}
        />

        <AccountsField
          emptyText={ADD_TO_ACCOUNT.accountEmpty}
          hasMore={hasMoreAccounts}
          help={ADD_TO_ACCOUNT.accountHelp}
          inputId="add-to-account-accounts-input"
          isLoading={isLoadingAccounts}
          label={ADD_TO_ACCOUNT.accountLabel}
          listId="add-to-account-accounts-list"
          loadingMoreText={ADD_TO_ACCOUNT.accountLoadingMore}
          options={accountOptions}
          pagedText={ADD_TO_ACCOUNT.accountPaged}
          searchPlaceholder={ADD_TO_ACCOUNT.accountSearchPlaceholder}
          selected={selectedAccountIds}
          toggleAriaLabel={ADD_TO_ACCOUNT.accountToggle}
          total={accountsTotal}
          onChange={onChangeSelectedAccounts}
          onLoadMore={onLoadMoreAccounts}
          onSearch={onSearchAccounts}
        />

        {error && (
          <div role="alert">
            <Alert
              intent="danger"
              title={ADD_TO_ACCOUNT.failedTitle}
              description={error}
            />
          </div>
        )}
      </Flex>
    </Modal>
  );
};

AddToAccountModal.propTypes = {
  accountOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  accountsTotal: PropTypes.number,
  email: PropTypes.string.isRequired,
  error: PropTypes.string,
  fullName: PropTypes.string.isRequired,
  hasMoreAccounts: PropTypes.bool,
  isLoadingAccounts: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onChangeSelectedAccounts: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoadMoreAccounts: PropTypes.func,
  onSearchAccounts: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedAccountIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  visible: PropTypes.bool,
};

AddToAccountModal.defaultProps = {
  accountOptions: [],
  accountsTotal: 0,
  error: null,
  hasMoreAccounts: false,
  isLoadingAccounts: false,
  isSubmitting: false,
  onLoadMoreAccounts: () => {},
  selectedAccountIds: [],
  visible: false,
};

export default AddToAccountModal;
